/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, forwardRef, useImperativeHandle, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { t } from 'i18next';

// Component imports
import { Button, Card, Col, Collapse, Divider, Empty, Form, Input, InputNumber, Row, Select, Slider, Space, Spin, Switch, Tabs, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import FormSectionHeader from '../../UI/FormSectionHeader';
import CustomInputNumber from '../Fields/CustomInputNumber';
import { DeleteIcon } from '../../Icons/DeleteIcon';
import { PlusIcon } from '../../Icons/PlusIcon';
import { ArrowRightIcon } from '../../Icons/ArrowRightIcon';

// Misc imports
import { EMBEDDING_PROVIDERS, LANGUAGE_OPTIONS, TEXT_GENERATION_PROVIDERS, TRANSLATION_PROVIDERS } from '../../../constants/main';
import { useBotLanguage } from '../../../utils/hooks/useBotLanguage';
import useTheme from '../../../utils/hooks/useTheme';

// Constant declarations
const { Text } = Typography;
const { Option } = Select;

const RAG_CONFIGURATION_TABS = {
  GENERAL_SETTINGS: 'general-settings',
  TRANSLATION: 'translation',
  EMBEDDINGS: 'embeddings',
  SEMANTIC_SEARCH: 'semantic-search',
  TEXT_GENERATION: 'text-generation',
  CHAT_HISTORY: 'chat-history',
  QUERY_SUMMARIZATION: 'query-summarization'
};

const TRANSLATION_PROVIDER_TYPES = {
  CLASSIC: 'CLASSIC',
  LLM: 'LLM'
};

const PARSER_TYPES = {
  JSON: 'JSON',
  REGEX: 'REGEX'
};

const HelviaRAGPipelineProviderForm = () => {
  return (
    <Fragment>
      <Divider orientation="left" className="mb-8">
        <Text type="secondary" className="fs-base">{t('Provider Settings')}</Text>
      </Divider>

      <Form.Item
        label={t('Service URL')}
        name="serviceUrl"
        rules={[{
          required: true,
          type: 'string',
          whitespace: true
        },
        {
          type: 'url',
          message: `${t('This field must be a valid url')}.`
        }]}
      >
        <Input placeholder={t('Enter the service url')} />
      </Form.Item>

      <Form.Item
        label={t('Pipeline ID')}
        name="pipelineId"
        rules={[{
          required: true,
          type: 'string',
          whitespace: true
        }]}
      >
        <Input placeholder={t('Enter the pipeline id')} />
      </Form.Item>

      <Form.Item
        label={t('Access Token')}
        name="bearerToken"
        rules={[{
          required: true,
          type: 'string',
          whitespace: true
        }]}
      >
        <Input.TextArea
          autoSize={{ minRows: 6, maxRows: 6 }}
          rows={4}
          placeholder={t('Enter the access token')}
        />
      </Form.Item>
    </Fragment>
  );
};

const HelviaRAGPipelineProviderConfigurationForm = forwardRef(({
  ragPipelineSettings,
  ragPipelineSettingsForm,
  isLoading,
  onSubmit,
  setHasPendingChanges,
  updateState
}, ref) => {
  const [activeTab, setActiveTab] = useState(RAG_CONFIGURATION_TABS.GENERAL_SETTINGS);
  const { primaryBotLanguage } = useBotLanguage();
  const { borderRadiusBase, formProps } = useTheme();

  const navigateToErrorField = (errorFieldName) => {
    let tabToNavigate = null;
    Object.values(tabItems).forEach(tab => {
      if(tab.sectionFields.some(section => errorFieldName?.includes(section))) {
        tabToNavigate = tab.key;
      }
    });

    if(tabToNavigate) {
      if(tabToNavigate !== activeTab){
        setActiveTab(tabToNavigate);
      }

      setTimeout(() => {
        ragPipelineSettingsForm.scrollToField(errorFieldName, {
          scrollMode: 'if-needed',
          behavior: 'smooth',
          block: 'center'
        });
      }, 500); // Small delay to ensure the tab content has rendered
    }
  };

  useImperativeHandle(ref, () => ({
    navigateToErrorField
  }));

  const handleTabClick = (tabKey) => {
    setActiveTab(tabKey);
  };

  const generateInitialValues = (settings) => {
    return {
      configuration: {
        ...settings.configuration,
        translation: {
          ...settings.configuration.translation,
          hide_urls: settings?.configuration?.translation?.hide_urls,
          corpus_translation_providers: transformGlossaries(settings.configuration?.translation?.corpus_translation_providers) || [],
          query_translation_providers: transformGlossaries(settings.configuration?.translation?.query_translation_providers) || [],
          response_translation_providers: transformGlossaries(settings.configuration?.translation?.response_translation_providers) || []
        }
      }
    };
  };

  // Transform the glossary dictionary to an array to correctly render inside the form
  const transformGlossaries = (providers) => {
    return providers?.map((provider) => {
      if (provider?.remote_glossaries) {
        let remoteGlossariesArray = Object.entries(provider.remote_glossaries)?.map(([key, value]) => ({ key, value: value.uri }));
        return {
          ...provider,
          remote_glossaries: remoteGlossariesArray
        };
      }
      return provider;
    });
  };

  const renderTranslationProvider = (field, type, remove, isDisabled = false, index) => {
    switch(type) {
      case TRANSLATION_PROVIDER_TYPES.CLASSIC:
        return (
          <Fragment>
            <Form.Item
              label={t('Provider')}
              name={[field.name, 'platform']}
              tooltip={{
                title: (
                  <Typography.Text type="secondary">{t('The provider to use for translations of incoming messages.\nIf more than one provider is configured, the service will use the next in the list when any error occurs while using one of them.')}</Typography.Text>
                ),
                icon: <InfoCircleOutlined />
              }}
            >
              <Select
                placeholder={t('Choose a provider')}
                disabled={isDisabled}
                getPopupContainer={triggerNode => triggerNode.parentElement} // Fix drop-menu position issues by rendering it in the parent element
              >
                {
                  Object.entries(TRANSLATION_PROVIDERS)
                    .map(([key, value]) => (
                      <Option key={key} value={value}>{t(value, { context: 'translation provider' })}</Option>
                    ))
                }
              </Select>
            </Form.Item>

            <Form.Item
              label={t('Location')}
              name={[field.name, 'location']}
              tooltip={{
                title: (
                  <Typography.Text type="secondary">{t('The location of the translation provider. Required by Google Translation Service and defaults to global if not set.')}</Typography.Text>
                ),
                icon: <InfoCircleOutlined />
              }}
            >
              <Input
                disabled={isDisabled}
                placeholder={t('Enter the corpus translation provider location')}
              />
            </Form.Item>

            <Space direction="vertical" size="small" className="border-radius-base w-100 bordered pt-3 pb-4 px-4 mb-4">
              <Text>{t('Glossaries:')}</Text>

              <Form.List
                name={[field.name, 'remote_glossaries']}
              >
                {(fields, { add, remove }) => (
                  <Fragment>
                    {fields.map((field) => {
                      return (
                        <Row gutter={16}>
                          <Col flex="auto">
                            <Form.Item
                              label={t('Key')}
                              name={[field.name, 'key']}
                            >
                              <Input disabled={isDisabled} placeholder={t('Enter the glossary key')}/>
                            </Form.Item>
                          </Col>

                          <Col flex="auto">
                            <Form.Item
                              label={t('Value')}
                              name={[field.name, 'value']}
                            >
                              <Input disabled={isDisabled} placeholder={t('Enter the glossary uri')} />
                            </Form.Item>
                          </Col>

                          <Col flex="none">
                            <Button disabled={isDisabled} type="text" onClick={() => remove(field.name)} icon={<DeleteIcon />} />
                          </Col>
                        </Row>
                      );
                    })}

                    <Button disabled={isDisabled} type="dashed" onClick={() => add()} icon={<PlusIcon />}>
                      {t('Add glossary')}
                    </Button>
                  </Fragment>
                )}
              </Form.List>
            </Space>
          </Fragment>
        );
      case TRANSLATION_PROVIDER_TYPES.LLM:
        return (
          renderProviderConfigurationForm(field, remove, isDisabled, index)
        );
      default:
        return null;
    }
  };

  const renderGeneralSettingsTabFormItems = () => {
    return (
      <Fragment>
        <Typography.Text type="secondary">{t('Contains general configurations such as language settings and article format.')}</Typography.Text>

        <Form.Item
          className="mt-5"
          label={t('Article Format')}
          name={['configuration', 'general_settings', 'article_format']}
          tooltip={{
            title: (
              <Row>
                <Col span={24}>
                  {t('The template to use when constructing the training text that represents each article.')}
                </Col>
                <Col span={24}>
                  {t('Available placeholders include:')}
                </Col>
                <Col span={24}>
                  {`- {{${t('title')}}}: ${'injects the title of the article'}`}
                </Col>
                <Col span={24}>
                  {`- {{${t('body')}}}: ${'injects the body of the article'}`}

                </Col>
                <Col span={24}>
                  {`- {{${t('group')}}}: ${'injects the group of the article'}`}

                </Col>
                <Col span={24}>
                  {`- {{${t('tags')}}}: ${'injects the tags of the article'}`}
                </Col>
                <Col span={24}>
                  {`- {{${t('examples')}}}: ${'injects examples of user input as defined in the article'}`}
                </Col>
              </Row>
            ),
            icon: <InfoCircleOutlined />
          }}
        >
          <Input.TextArea
            autoSize={{ minRows: 6, maxRows: 32 }}
          />
        </Form.Item>

        <Form.Item
          label={t('Corpus Language')}
          name={['configuration', 'general_settings', 'corpus_language']}
          tooltip={{
            title: (
              <Typography.Text type="secondary">{t('The language which we expect the corpus of the pipeline')}</Typography.Text>
            ),
            icon: <InfoCircleOutlined />
          }}
        >
          <Select
            placeholder={t('Choose a language')}
            dropdownMatchSelectWidth={false}
            defaultValue={LANGUAGE_OPTIONS.find((language) => language.key === 'en')}
            showSearch
            allowClear="true"
            filterOption={(inputValue, option) => option.searchValue.toLowerCase()?.includes(inputValue.toLowerCase())}
            options={
              LANGUAGE_OPTIONS.map(lang => ({
                label: <Space size={4}>{lang?.icon} {lang.label} {lang.key === primaryBotLanguage ? <Text type="secondary">{t('(primary)')}</Text> : null}</Space>,
                value: lang.key,
                searchValue: lang.label
              }
              ))
            }
            getPopupContainer={triggerNode => triggerNode.parentElement}
          />
        </Form.Item>

        <Form.Item
          label={t('Default Native Language')}
          name={['configuration', 'general_settings', 'default_native_language']}
          tooltip={{
            title: (
              <Typography.Text type="secondary">{t('This is the target language that is used when translating text that is a non-native language')}</Typography.Text>
            ),
            icon: <InfoCircleOutlined />
          }}
        >
          <Select
            placeholder={t('Choose a language')}
            dropdownMatchSelectWidth={false}
            defaultValue={LANGUAGE_OPTIONS.find((language) => language.key === 'en')}
            showSearch
            allowClear="true"
            filterOption={(inputValue, option) => option.searchValue.toLowerCase()?.includes(inputValue.toLowerCase())}
            options={
              LANGUAGE_OPTIONS.map(lang => ({
                label: <Space size={4}>{lang?.icon} {lang.label} {lang.key === primaryBotLanguage ? <Text type="secondary">{t('(primary)')}</Text> : null}</Space>,
                value: lang.key,
                searchValue: lang.label
              }
              ))
            }
            getPopupContainer={triggerNode => triggerNode.parentElement}
          />
        </Form.Item>

        <Form.Item
          label={t('Native Languages')}
          name={['configuration', 'general_settings', 'native_languages']}
          tooltip={{
            title: (
              <Typography.Text type="secondary">{t('A list of languages that are considered native to the LLMs and don\'t require translation')}</Typography.Text>
            ),
            icon: <InfoCircleOutlined />
          }}
        >
          <Select
            mode="multiple"
            placeholder={t('Choose native languages')}
            dropdownMatchSelectWidth={false}
            defaultValue={[LANGUAGE_OPTIONS.find((language) => language.key === 'en')]}
            filterOption={(inputValue, option) => option.searchValue?.includes(inputValue.toLowerCase())}
            options= {LANGUAGE_OPTIONS.map(lang => ({
              value: lang.key,
              label: (
                <Space size={5}>
                  {lang.label}
                </Space>
              ),
              searchValue: lang.label.toLowerCase()
            }))}
            getPopupContainer={triggerNode => triggerNode.parentElement}
          />
        </Form.Item>
      </Fragment>
    );
  };

  const renderTranslationTabFormItems = () => {
    const isTranslationsEnabled = ragPipelineSettingsForm.getFieldValue(['configuration', 'translation', 'enabled']);

    return (
      <Fragment>
        <Typography.Text type="secondary">{t('Contains configuration of translation providers that can be used when translations are needed within the various actions of this pipeline.')}</Typography.Text>

        <Form.Item
          label={t('Enable Translations')}
          className="mt-5"
          name={['configuration', 'translation', 'enabled']}
          valuePropName="checked"
        >
          <Switch/>
        </Form.Item>

        <Form.Item
          hidden={!isTranslationsEnabled}
          label={t('Hide URLs')}
          className="mt-5"
          name={['configuration', 'translation', 'hide_urls']}
          valuePropName="checked"
          tooltip={{
            title: (
              <Typography.Text type="secondary">{t('When enabled, replaces urls with UUIDs before translations. URLs are then restored after translation.')}</Typography.Text>
            ),
            icon: <InfoCircleOutlined />
          }}
        >
          <Switch disabled={!isTranslationsEnabled} />
        </Form.Item>

        {
          !isTranslationsEnabled ? null : (
            <Divider orientation="left" className="mb-5">
              <Text type="secondary" className="fs-base">{t('Corpus Translation')}</Text>
            </Divider>
          )
        }

        <Form.List
          name={['configuration', 'translation', 'corpus_translation_providers']}
        >
          {(fields, { add, remove }) => (
            <Fragment>
              {fields.map((field, index) => {
                return (
                  <Card
                    hidden={!isTranslationsEnabled}
                    className="bg-layout-gray shadow-none position-relative mb-4"
                    key={field.key}
                  >
                    <Form.Item
                      label={t('Type')}
                      name={[field.name, 'type']}
                      required
                      rules={[
                        {
                          validator: async (_, value) => {
                            if(!value) {
                              if(!isTranslationsEnabled) {
                                try {
                                  remove?.(index);
                                  return Promise.resolve();
                                } catch(e) {
                                  return;
                                }
                              }
                              return Promise.reject(new Error('Field is required'));
                            }
                          }
                        }
                      ]}
                      tooltip={{
                        title: (
                          <Typography.Text type="secondary">{t('Translation provider type.')}</Typography.Text>
                        ),
                        icon: <InfoCircleOutlined />
                      }}
                    >
                      <Select
                        disabled={!isTranslationsEnabled}
                        placeholder={t('Choose a provider type')}
                        getPopupContainer={triggerNode => triggerNode.parentElement}
                      >
                        {
                          Object.entries(TRANSLATION_PROVIDER_TYPES)
                            .map(([key, value]) => (
                              <Option key={key} value={value}>{t(value, { context: 'translation provider type' })}</Option>
                            ))
                        }
                      </Select>
                    </Form.Item>

                    {renderTranslationProvider(field, ragPipelineSettingsForm.getFieldValue(['configuration', 'translation', 'corpus_translation_providers', field.name, 'type']), remove, !isTranslationsEnabled, index)}

                    <Row justify="end">
                      <Col>
                        <Button
                          disabled={!isTranslationsEnabled}
                          shape="circle"
                          type="text"
                          icon={<DeleteIcon />}
                          className="bordered border-radius-base"
                          onClick={() => remove(field.name)}
                        />
                      </Col>
                    </Row>
                  </Card>
                );
              })}

              <Form.Item
                hidden={!isTranslationsEnabled}
              >
                <Button type="dashed" className="bg-layout-gray" disabled={!isTranslationsEnabled} onClick={() => add()} icon={<PlusIcon />}>
                  {t('Add a Provider')}
                </Button>
              </Form.Item>
            </Fragment>
          )}
        </Form.List>

        {
          !isTranslationsEnabled ? null : (
            <Divider orientation="left" className="mb-8">
              <Text type="secondary" className="fs-base">{t('Query Translation')}</Text>
            </Divider>
          )
        }

        <Form.List
          name={['configuration', 'translation', 'query_translation_providers']}
        >
          {(fields, { add, remove }) => (
            <Fragment>
              {fields.map((field, index) => {
                return (
                  <Card
                    hidden={!isTranslationsEnabled}
                    className="bg-layout-gray shadow-none position-relative mb-4"
                    key={field.key}
                  >
                    <Form.Item
                      label={t('Type')}
                      name={[field.name, 'type']}
                      required
                      rules={[
                        {
                          validator: async (_, value) => {
                            if(!value) {
                              if(!isTranslationsEnabled) {
                                try {
                                  remove?.(index);
                                  return Promise.resolve();
                                } catch(e) {
                                  return;
                                }
                              }
                              return Promise.reject(new Error('Field is required'));
                            }
                          }
                        }
                      ]}
                      tooltip={{
                        title: (
                          <Typography.Text type="secondary">{t('Translation provider type.')}</Typography.Text>
                        ),
                        icon: <InfoCircleOutlined />
                      }}
                    >
                      <Select
                        disabled={!isTranslationsEnabled}
                        placeholder={t('Choose a provider type')}
                        getPopupContainer={triggerNode => triggerNode.parentElement}
                      >
                        {
                          Object.entries(TRANSLATION_PROVIDER_TYPES)
                            .map(([key, value]) => (
                              <Option key={key} value={value}>{t(value, { context: 'translation provider type' })}</Option>
                            ))
                        }
                      </Select>
                    </Form.Item>

                    {renderTranslationProvider(field, ragPipelineSettingsForm.getFieldValue(['configuration', 'translation', 'query_translation_providers', field.name, 'type']), remove, !isTranslationsEnabled, index)}

                    <Row justify="end">
                      <Col>
                        <Button
                          disabled={!isTranslationsEnabled}
                          shape="circle"
                          type="text"
                          icon={<DeleteIcon />}
                          className="bordered border-radius-base"
                          onClick={() => remove(field.name)}
                        />
                      </Col>
                    </Row>
                  </Card>
                );
              })}

              <Form.Item
                hidden={!isTranslationsEnabled}
              >
                <Button type="dashed" className="bg-layout-gray" disabled={!isTranslationsEnabled} onClick={() => add()} icon={<PlusIcon />}>
                  {t('Add a Provider')}
                </Button>
              </Form.Item>
            </Fragment>
          )}
        </Form.List>

        {
          !isTranslationsEnabled ? null : (
            <Divider orientation="left" className="mb-8">
              <Text type="secondary" className="fs-base">{t('Response Translation')}</Text>
            </Divider>
          )
        }

        <Form.List
          name={['configuration', 'translation', 'response_translation_providers']}
        >
          {(fields, { add, remove }) => (
            <Fragment>
              {fields.map((field, index) => {
                return (
                  <Card
                    hidden={!isTranslationsEnabled}
                    className="bg-layout-gray shadow-none position-relative mb-4"
                    key={field.key}
                  >
                    <Form.Item
                      label={t('Type')}
                      name={[field.name, 'type']}
                      required
                      rules={[
                        {
                          validator: async (_, value) => {
                            if(!value) {
                              if(!isTranslationsEnabled) {
                                try {
                                  remove?.(index);
                                  return Promise.resolve();
                                } catch(e) {
                                  return;
                                }
                              }
                              return Promise.reject(new Error('Field is required'));
                            }
                          }
                        }
                      ]}
                      tooltip={{
                        title: (
                          <Typography.Text type="secondary">{t('Translation provider type.')}</Typography.Text>
                        ),
                        icon: <InfoCircleOutlined />
                      }}
                    >
                      <Select
                        disabled={!isTranslationsEnabled}
                        placeholder={t('Choose a provider type')}
                        getPopupContainer={triggerNode => triggerNode.parentElement}
                      >
                        {
                          Object.entries(TRANSLATION_PROVIDER_TYPES)
                            .map(([key, value]) => (
                              <Option key={key} value={value}>{t(value, { context: 'translation provider type' })}</Option>
                            ))
                        }
                      </Select>
                    </Form.Item>

                    {renderTranslationProvider(field, ragPipelineSettingsForm.getFieldValue(['configuration', 'translation', 'response_translation_providers', field.name, 'type']), remove, !isTranslationsEnabled, index)}

                    <Row justify="end">
                      <Col>
                        <Button
                          disabled={!isTranslationsEnabled}
                          shape="circle"
                          type="text"
                          icon={<DeleteIcon />}
                          className="bordered border-radius-base"
                          onClick={() => remove(field.name)}
                        />
                      </Col>
                    </Row>
                  </Card>
                );
              })}

              <Form.Item
                hidden={!isTranslationsEnabled}

              >
                <Button type="dashed" className="bg-layout-gray" disabled={!isTranslationsEnabled} onClick={() => add()} icon={<PlusIcon />}>
                  {t('Add a Provider')}
                </Button>
              </Form.Item>
            </Fragment>
          )}
        </Form.List>
      </Fragment>
    );
  };

  const renderSemanticSearchTabFormItems = () => {
    const isSemanticSearchEnabled = ragPipelineSettingsForm.getFieldValue(['configuration', 'semantic_search', 'enabled']);

    return (
      <Fragment>
        <Typography.Text type="secondary">{t('Contains configuration for the retrieval phase of RAG (e.g. top-k).')}</Typography.Text>

        <Form.Item
          label={t('Enable Semantic Search')}
          className="mt-5"
          name={['configuration', 'semantic_search', 'enabled']}
          valuePropName="checked"
        >
          <Switch className="ml-3" />
        </Form.Item>

        <Form.Item
          hidden={!isSemanticSearchEnabled}
          label={t('Max Results')}
          name={['configuration', 'semantic_search', 'max_results']}
        >
          <Slider
            min={0}
            max={20}
            className="custom-slider"
            marks={{ 0: '0', 20: '20' }}
            handleStyle={{ borderRadius: borderRadiusBase }}
          />
        </Form.Item>

        <Form.Item
          hidden={!isSemanticSearchEnabled}
          label={t('Normalize Input')}
          name={['configuration', 'semantic_search', 'normalize_user_input']}
          valuePropName="checked"
          tooltip={{
            icon: <InfoCircleOutlined />,
            title: (
              <Typography.Text type="secondary">{t('Normalize user input when performing semantic search.')}</Typography.Text>
            )
          }}
        >
          <Switch />
        </Form.Item>

        <Form.Item
          hidden={!isSemanticSearchEnabled}
          label={t('Normalize Corpus')}
          name={['configuration', 'semantic_search', 'normalize_corpus']}
          valuePropName="checked"
          tooltip={{
            icon: <InfoCircleOutlined />,
            title: (
              <Typography.Text type="secondary">{t('Normalize corpus when indexing.')}</Typography.Text>
            )
          }}
        >
          <Switch />
        </Form.Item>

        <Form.Item
          hidden={!isSemanticSearchEnabled}
          label={t('Max Input Tokens')}
          name={['configuration', 'semantic_search', 'max_input_tokens']}
          tooltip={{
            icon: <InfoCircleOutlined />,
            title: (
              <Typography.Text type="secondary">{t('Limit results by total tokens (when set, has priority over max_results).')}</Typography.Text>
            )
          }}
        >
          <CustomInputNumber min={0} />
        </Form.Item>

        <Form.Item
          hidden={!isSemanticSearchEnabled}
          label={t('Exact Match')}
          name={['configuration', 'semantic_search', 'exact_match']}
          valuePropName="checked"
          tooltip={{
            icon: <InfoCircleOutlined />,
            title: (
              <Typography.Text type="secondary">{t('Prefer exact match when searching (results in slower performance).')}</Typography.Text>
            )
          }}
        >
          <Switch />
        </Form.Item>

        <Form.Item
          hidden={!isSemanticSearchEnabled}
          label={t('Visit Neighbors')}
          name={['configuration', 'semantic_search', 'visit_neighbors']}
          tooltip={{
            icon: <InfoCircleOutlined />,
            title: (
              <Typography.Text type="secondary">{t('Number of neighbors to visit during semantic search (higher value leads to improved accuracy but results in slower performance).')}</Typography.Text>
            )
          }}
        >
          <CustomInputNumber min={1} />
        </Form.Item>

        <Collapse
          ghost
          className={classNames('pb-4', { 'd-none': !isSemanticSearchEnabled })}
          defaultActiveKey={'1'}
        >
          <Collapse.Panel
            showArrow={false}
            header={
              <FormSectionHeader
                uppercase={false}
                title={t('Embeddings Providers')}
              />
            }
            key="1"
          >
            <Typography.Text type="secondary">{t('Contains configuration of embeddings providers that are used when vectorizing corpus or queries.')}</Typography.Text>

            <Form.List
              name={['configuration', 'embeddings', 'providers']}
            >
              {(fields, { add, remove }) => (
                <>
                  {fields.length === 0 ? add() : null}

                  {fields.map((field, index) => {
                    return (
                      <Fragment>
                        {field.name > 0
                          ? <Divider orientation="left" className="mt-0 mb-5"/> : null
                        }
                        <Card
                          hidden={!isSemanticSearchEnabled}
                          className="bg-layout-gray shadow-none position-relative mb-2 mt-2"
                          key={field.key}
                        >
                          { renderProviderConfigurationForm(field, remove, !isSemanticSearchEnabled, index)}

                          <Row justify="end">
                            <Col>
                              <Button
                                disabled={!isSemanticSearchEnabled}
                                shape="circle"
                                type="text"
                                icon={<DeleteIcon />}
                                className="bordered border-radius-base"
                                onClick={() => remove(field.name)}
                              />
                            </Col>
                          </Row>
                        </Card>
                      </Fragment>
                    );
                  })}

                  <Button type="dashed" disabled={!isSemanticSearchEnabled} className="w-30 bg-layout-gray" onClick={() => add()} block icon={<PlusIcon />}>
                    {t('Add a Provider')}
                  </Button>
                </>
              )}
            </Form.List>
          </Collapse.Panel>
        </Collapse>
      </Fragment>
    );
  };

  const renderTextGenerationTabFormItems = () => {
    return (
      <Fragment>
        <Typography.Text type="secondary">{t('Contains configuration of LLM providers.')}</Typography.Text>
        <Form.List
          name={['configuration', 'text_generation', 'providers']}
        >
          {(fields, { add, remove }) => (
            <Fragment>
              <Divider orientation="left" className="mb-5">
                <Text type="secondary" className="fs-base">{t('Text Generation Providers')}</Text>
              </Divider>

              {fields.length === 0 ? add() : null}
              {fields.map((field) => {
                return (
                  <Fragment>
                    {field.name > 0
                      ? <Divider orientation="left" className="mt-0 mb-5"/> : null
                    }
                    <Card
                      className="bg-layout-gray shadow-none position-relative mb-2 mt-2"
                      key={field.key}
                    >
                      { renderProviderConfigurationForm(field, remove)}
                      <Row justify="end">
                        <Col>
                          <Button
                            shape="circle"
                            type="text"
                            icon={<DeleteIcon />}
                            className="bordered border-radius-base"
                            onClick={() => remove(field.name)}
                          />
                        </Col>
                      </Row>
                    </Card>
                  </Fragment>
                );
              })}

              <Button type="dashed" className="bg-layout-gray w-30" onClick={() => add()} block icon={<PlusIcon />}>
                {t('Add a Provider')}
              </Button>
            </Fragment>
          )}
        </Form.List>

        <Form.Item
          className="mt-5"
          label={t('Prompt')}
          name={['configuration', 'text_generation', 'prompt']}
          tooltip={{
            title: (
              <Typography.Text type="secondary">{t('The prompt to use when invoking the LLM.')}</Typography.Text>
            ),
            icon: <InfoCircleOutlined />
          }}
        >
          <Input.TextArea
            rows={8}
            placeholder={t('Enter the prompt')}
          />
        </Form.Item>

        <Form.Item
          label={t('Temperature')}
          name={['configuration', 'text_generation', 'temperature']}
          tooltip={{
            title: (
              <Typography.Text type="secondary">{t('The prompt to use when invoking the LLM.')}</Typography.Text>
            ),
            icon: <InfoCircleOutlined />
          }}
        >
          <CustomInputNumber min={0} max={1} step={0.1} defaultValue={0.2} />
        </Form.Item>

        <Form.Item
          label={t('Max Tokens')}
          name={['configuration', 'text_generation', 'max_tokens']}
          tooltip={{
            title: (
              <Typography.Text type="secondary">{t('The maximum number of tokens to use when invoking the LLM.')}</Typography.Text>
            ),
            icon: <InfoCircleOutlined />
          }}
        >
          <CustomInputNumber min={1} defaultValue={50}/>
        </Form.Item>

        <Divider orientation="left" className="mb-5">
          <Text type="secondary" className="fs-base">{t('Parser')}</Text>
        </Divider>

        <Form.Item
          label={t('Type')}
          name={['configuration', 'text_generation', 'parser', 'type']}
          tooltip={{
            title: (
              <Typography.Text type="secondary">{t('TEXT parsers must also define a regular expression which will be used to extract the query summarization from the LLM response.')}</Typography.Text>
            ),
            icon: <InfoCircleOutlined />
          }}
        >
          <Select
            placeholder={t('Choose a parser type')}
            getPopupContainer={triggerNode => triggerNode.parentElement}
          >
            {
              Object.entries(PARSER_TYPES)
                .map(([key, value]) => (
                  <Option key={key} value={value}>{t(value)}</Option>
                ))
            }
          </Select>
        </Form.Item>

        {
          ragPipelineSettingsForm.getFieldValue(['configuration', 'text_generation', 'parser', 'type']) === PARSER_TYPES.REGEX ? (
            <Form.Item
              label={t('Regex')}
              name={['configuration', 'text_generation', 'parser', 'regex']}
              tooltip={{
                title: (
                  <Typography.Text type="secondary">{t('The regex to apply when parsing the llm response. If None is set, the default will be used:(ID:(.?))\\s(.*?)$.')}</Typography.Text>
                ),
                icon: <InfoCircleOutlined />
              }}
            >
              <Input placeholder={t('Enter the regex')} />
            </Form.Item>
          ) : null
        }
      </Fragment>
    );
  };

  const renderChatHistoryTabFormItems = () => {
    const isChatHistoryEnabled = ragPipelineSettingsForm.getFieldValue(['configuration', 'chat_history', 'enabled']);

    if(!isChatHistoryEnabled) {
      ragPipelineSettingsForm.setFieldValue(['configuration', 'query_summarization', 'enabled'], false);
    }

    const isQuerySummarizationEnabled = ragPipelineSettingsForm.getFieldValue(['configuration', 'chat_history', 'enabled']) && ragPipelineSettingsForm.getFieldValue(['configuration', 'query_summarization', 'enabled']);

    return (
      <Fragment>
        <Typography.Text type="secondary">{t('Contains settings for the local chat history, such as the number of messages to keep.')}</Typography.Text>

        <Form.Item
          label="Enable Chat History"
          name={['configuration', 'chat_history', 'enabled']}
          valuePropName="checked"
          className="mt-4"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          hidden={!isChatHistoryEnabled}
          label={t('Max Messages')}
          name={['configuration', 'chat_history', 'max_messages']}
          tooltip={{
            title: (
              <Typography.Text type="secondary">{t('The max number of messages to keep in chat history per session.')}</Typography.Text>
            ),
            icon: <InfoCircleOutlined />
          }}
        >
          <Slider
            disabled={!isChatHistoryEnabled}
            min={0}
            max={20}
            className="custom-slider"
            marks={{ 0: '0', 20: '20' }}
            handleStyle={{ borderRadius: borderRadiusBase }}
            defaultValue={5}
          />
        </Form.Item>

        <Collapse
          ghost
          className={classNames('pb-4', { 'd-none': !isChatHistoryEnabled })}
          defaultActiveKey={'1'}
        >
          <Collapse.Panel
            showArrow={false}
            header={
              <FormSectionHeader
                uppercase={false}
                title={t('Query Summarization Providers')}
              />
            }
            key="1"
          >
            <Space.Compact direction="vertical">
              <Typography.Text type="secondary">{t('Contains settings for enabling user query summarization.')}</Typography.Text>
              <Typography.Text type="secondary">{t('Query summarization (aka history reduction) uses the previous conversation to rewrite the last user message with more context from the previous messages.')}</Typography.Text>
              <Typography.Text type="secondary">{t('This technique can be used to enable history in RAG architectures, when queries may refer to previous answers.Works only in conjunction with chat history.')}</Typography.Text>
            </Space.Compact>

            <Form.Item
              label="Enable"
              name={['configuration', 'query_summarization', 'enabled']}
              valuePropName="checked"
              className="mt-4"
            >
              <Switch />
            </Form.Item>

            <Form.List
              name={['configuration', 'query_summarization', 'providers']}
            >
              {(fields, { add, remove }) => (
                <Fragment>

                  {fields.map((field, index) => {
                    return (
                      <Fragment>
                        {field.name > 0
                          ? <Divider orientation="left" className="mt-0 mb-5"/> : null
                        }
                        <Card
                          className="bg-layout-gray shadow-none position-relative mb-2 mt-2"
                          key={field.key}
                        >
                          { renderProviderConfigurationForm(field, remove, !isQuerySummarizationEnabled, index)}
                          <Row justify="end">
                            <Col>
                              <Button
                                disabled={!isQuerySummarizationEnabled}
                                shape="circle"
                                type="text"
                                icon={<DeleteIcon />}
                                className="bordered border-radius-base"
                                onClick={() => remove(field.name)}
                              />
                            </Col>
                          </Row>
                        </Card>
                      </Fragment>
                    );
                  })}

                  <Button
                    type="dashed"
                    className="bg-layout-gray w-30"
                    onClick={() => add()}
                    block
                    icon={<PlusIcon />}
                    disabled={!isQuerySummarizationEnabled}
                  >
                    {t('Add a Provider')}
                  </Button>
                </Fragment>
              )}
            </Form.List>

            <Form.Item
              className="mt-5"
              label={t('Prompt')}
              name={['configuration', 'query_summarization', 'prompt']}
              tooltip={{
                title: (
                  <Typography.Text type="secondary">{t('The prompt to use when invoking the LLM.')}</Typography.Text>
                ),
                icon: <InfoCircleOutlined />
              }}
            >
              <Input.TextArea
                disabled={!isQuerySummarizationEnabled}
                rows={8}
                placeholder={t('Enter the prompt')}
              />
            </Form.Item>

            <Form.Item
              className="mt-5"
              label={t('Query')}
              name={['configuration', 'query_summarization', 'query']}
              tooltip={{
                title: (
                  <Typography.Text type="secondary">{t('The template of the query to send sending the request to the LLM.\nAvailable placeholders: - {{dialog}}: the current dialog constructed using previous (history) messages.')}</Typography.Text>
                ),
                icon: <InfoCircleOutlined />
              }}
            >
              <Input.TextArea
                disabled={!isQuerySummarizationEnabled}
                rows={8}
                placeholder={t('Enter the query')}
              />
            </Form.Item>

            <Form.Item
              label={t('Temperature')}
              name={['configuration', 'query_summarization', 'temperature']}
              tooltip={{
                title: (
                  <Typography.Text type="secondary">{t('The temperature to use when invoking the LLM.')}</Typography.Text>
                ),
                icon: <InfoCircleOutlined />
              }}
            >
              <Slider
                disabled={!isQuerySummarizationEnabled}
                min={0}
                max={1}
                step={0.1}
                className="custom-slider"
                marks={{ 0: '0', 1: '1' }}
                defaultValue={0.2}
                handleStyle={{ borderRadius: borderRadiusBase }}
              />
            </Form.Item>

            <Form.Item
              label={t('Max Tokens')}
              name={['configuration', 'query_summarization', 'max_tokens']}
              tooltip={{
                title: (
                  <Typography.Text type="secondary">{t('The maximum number of tokens to use when invoking the LLM.')}</Typography.Text>
                ),
                icon: <InfoCircleOutlined />
              }}
            >
              <CustomInputNumber
                min={1}
                defaultValue={50}
                step={1}
                disabled={!isQuerySummarizationEnabled}
              />
            </Form.Item>

            <Form.Item
              label={t('Skip History')}
              className="mt-5"
              name={['configuration', 'query_summarization', 'skip_history_in_inference']}
              valuePropName="checked"
              tooltip={{
                title: (
                  <Typography.Text type="secondary">{t('If query summarization is enabled and this property is set to True, the history of the dialog will not be used during inference.')}</Typography.Text>
                ),
                icon: <InfoCircleOutlined />
              }}
            >
              <Switch
                defaultChecked
                disabled={!isQuerySummarizationEnabled}
              />
            </Form.Item>

            <Divider orientation="left" className="mb-5">
              <Text type="secondary" className="fs-base">{t('Parser')}</Text>
            </Divider>

            <Form.Item
              label={t('Type')}
              name={['configuration', 'query_summarization', 'parser', 'type']}
              tooltip={{
                title: (
                  <Typography.Text type="secondary">{t('TEXT parsers must also define a regular expression which will be used to extract the query summarization from the LLM response')}</Typography.Text>
                ),
                icon: <InfoCircleOutlined />
              }}
            >
              <Select
                disabled={!isQuerySummarizationEnabled}
                placeholder={t('Choose a parser type')}
                getPopupContainer={triggerNode => triggerNode.parentElement}
              >
                {
                  Object.entries(PARSER_TYPES)
                    .map(([key, value]) => (
                      <Option key={key} value={value}>{t(value)}</Option>
                    ))
                }
              </Select>
            </Form.Item>

            {
              ragPipelineSettingsForm.getFieldValue(['configuration', 'query_summarization', 'parser', 'type']) === PARSER_TYPES.REGEX ? (
                <Form.Item
                  label={t('Regex')}
                  name={['configuration', 'query_summarization', 'parser', 'regex']}
                  tooltip={{
                    title: (
                      <Typography.Text type="secondary">{t('The regex to apply when parsing the llm response. If None is set, the default will be used:(ID:(.?))\\s(.*?)$.')}</Typography.Text>
                    ),
                    icon: <InfoCircleOutlined />
                  }}
                >
                  <Input
                    disabled={!isQuerySummarizationEnabled}
                    placeholder={t('Enter the regex')}
                  />
                </Form.Item>
              ) : null
            }
          </Collapse.Panel>
        </Collapse>
      </Fragment>
    );
  };

  const tabItems = {
    GENERAL_SETTINGS: {
      label: t('General Settings'),
      priority: 1,
      key: RAG_CONFIGURATION_TABS.GENERAL_SETTINGS,
      children: renderGeneralSettingsTabFormItems(),
      sectionFields: ['general_settings']
    },
    TRANSLATION: {
      label: t('Translation'),
      priority: 2,
      key: RAG_CONFIGURATION_TABS.TRANSLATION,
      children: renderTranslationTabFormItems(),
      sectionFields: ['translation']
    },
    SEMANTIC_SEARCH: {
      label: t('Semantic Search'),
      priority: 3,
      key: RAG_CONFIGURATION_TABS.SEMANTIC_SEARCH,
      children: renderSemanticSearchTabFormItems(),
      sectionFields: ['semantic_search', 'embeddings']
    },
    TEXT_GENERATION: {
      label: t('Text Generation'),
      priority: 4,
      key: RAG_CONFIGURATION_TABS.TEXT_GENERATION,
      children: renderTextGenerationTabFormItems(),
      sectionFields: ['text_generation']
    },
    CHAT_HISTORY: {
      label: t('Chat History'),
      priority: 5,
      key: RAG_CONFIGURATION_TABS.CHAT_HISTORY,
      children: renderChatHistoryTabFormItems(),
      sectionFields: ['chat_history', 'query_summarization']
    }
  };

  const renderProviderConfigurationForm = (field, remove, isDisabled = false, index) => {
    return (
      <Fragment>
        <Form.Item
          className="mt-5"
          label={t('Provider')}
          name={[field.name, 'platform']}
          required
          rules={[
            {
              validator: async (_, value) => {
                if(!value) {
                  if(isDisabled) {
                    try {
                      remove?.(index);
                      return Promise.resolve();
                    } catch(e) {
                      return;
                    }
                  }
                  return Promise.reject(new Error('Field is required'));
                }
              }
            }
          ]}
          tooltip={{
            title: (
              <Typography.Text type="secondary">{t('The provider to use for translations of incoming messages.\nIf more than one provider is configured, the service will use the next in the list when any error occurs while using one of them.')}</Typography.Text>
            ),
            icon: <InfoCircleOutlined />
          }}
        >
          <Select
            className="w-100"
            placeholder={t('Choose a provider')}
            disabled={isDisabled}
            getPopupContainer={triggerNode => triggerNode.parentElement}
          >
            {
              (() => {
                switch (activeTab) {
                  case tabItems.TEXT_GENERATION.key:
                    return <Fragment>
                      {
                        Object.entries(TEXT_GENERATION_PROVIDERS)
                          .map(([key, value]) => (
                            <Option key={key} value={value}>{t(value, { context: 'generation provider' })}</Option>
                          ))
                      }
                    </Fragment>;
                  default:
                    return <Fragment>
                      {
                        Object.entries(EMBEDDING_PROVIDERS)
                          .map(([key, value]) => (
                            <Option key={key} value={value}>{t(value, { context: 'embedding provider' })}</Option>
                          ))
                      }
                    </Fragment>;
                }
              })()
            }
          </Select>
        </Form.Item>

        <Form.Item
          label={t('Provider URL')}
          name={[field.name, 'platform_url']}
          required
          rules={[
            {
              validator: async (_, value) => {
                if(!value) {
                  if(isDisabled) {
                    try {
                      remove?.(index);
                      return Promise.resolve();
                    } catch(e) {
                      return;
                    }
                  }
                  return Promise.reject(new Error('Field is required'));
                }
              }
            }
          ]}
        >
          <Input disabled={isDisabled} placeholder={t('Enter the provider URL')} />
        </Form.Item>

        <Form.Item
          label={t('Provider API Key')}
          name={[field.name, 'platform_api_key']}
          required
          rules={[
            {
              validator: async (_, value) => {
                if(!value) {
                  if(isDisabled) {
                    try {
                      remove?.(index);
                      return Promise.resolve();
                    } catch(e) {
                      return;
                    }
                  }
                  return Promise.reject(new Error('Field is required'));
                }
              }
            }
          ]}
        >
          <Input disabled={isDisabled} placeholder={t('Enter the provider API key')} />
        </Form.Item>

        <Form.Item
          label={t('Model')}
          name={[field.name, 'model']}
          required
          rules={[
            {
              validator: async (_, value) => {
                if(!value) {
                  if(isDisabled) {
                    try {
                      remove?.(index);
                      return Promise.resolve();
                    } catch(e) {
                      return;
                    }
                  }
                  return Promise.reject(new Error('Field is required'));
                }
              }
            }
          ]}
          tooltip={{
            title: (
              <Typography.Text type="secondary">{t('The model of the NLP Provider.')}</Typography.Text>
            ),
            icon: <InfoCircleOutlined />
          }}
        >
          <Input disabled={isDisabled} placeholder={t('Enter the model')} />
        </Form.Item>

        <Form.Item
          label={t('Seed')}
          name={[field.name, 'seed']}
          tooltip={{
            title: (
              <Typography.Text type="secondary">{t('If specified, some NLP Providers will make a best effort to sample deterministically.')}</Typography.Text>
            ),
            icon: <InfoCircleOutlined />
          }}
        >
          <InputNumber disabled={isDisabled} min={0} className="w-100" placeholder={t('Enter the provider seed')} />
        </Form.Item>

        {
          activeTab === RAG_CONFIGURATION_TABS.TRANSLATION ? (
            <Fragment>
              <Form.Item
                className="mt-5"
                label={t('Prompt')}
                name={[field.name, 'prompt']}
                tooltip={{
                  title: (
                    <Typography.Text type="secondary">{t('The prompt to use when invoking the LLM.')}</Typography.Text>
                  ),
                  icon: <InfoCircleOutlined />
                }}
              >
                <Input.TextArea
                  disabled={isDisabled}
                  rows={8}
                  placeholder={t('Enter the prompt')}
                />
              </Form.Item>
            </Fragment>
          ) : null
        }
      </Fragment>
    );
  };

  return (
    <Fragment>
      {
        ragPipelineSettings && !isLoading ? (
          <Form
            form={ragPipelineSettingsForm}
            validateMessages={{
              required: t('Please enter a value'),
              whitespace: t('Value cannot be blank')
            }}
            initialValues={ragPipelineSettings ? generateInitialValues(ragPipelineSettings) : {}}
            validateTrigger={['onBlur', 'onChange', 'onSubmit', 'onFinish']}
            onFinish={onSubmit}
            onValuesChange={(_, allFields) => {
              setHasPendingChanges(true);
              updateState(allFields);
            }}
            labelCol={{ xs: activeTab === RAG_CONFIGURATION_TABS.GENERAL_SETTINGS ? 8 : formProps.mainColSpan }}
            wrapperCol={{ xs: activeTab === RAG_CONFIGURATION_TABS.GENERAL_SETTINGS ? 16 : formProps.secondaryColSpan }}
            labelAlign="left"
          >
            <Tabs
              activeKey={activeTab}
              onTabClick={(key) => {
                handleTabClick(key);
              }}
              defaultActiveKey={Object.values(tabItems).find(tab => tab.priority === 1)}
              tabPosition="left"
              className="rag-pipeline-tabs mb-5"
              items={
                Object.values(tabItems)
                  .sort((a, b) => a.priority - b.priority)
                  .map(tab => ({
                    label: (
                      <Row>
                        <Col xs={24}>
                          <Space className="tab-item d-flex justify-content-between">
                            <Typography.Text className={activeTab === tab.key && 'text-link fw-400'}>{tab.label}</Typography.Text>
                            {activeTab === tab.key && <ArrowRightIcon className="text-link mr-0"/>}
                          </Space>
                        </Col>
                      </Row>
                    ),
                    key: tab.key,
                    children: tab.children
                  }))
              }
            />
          </Form>
        ) : ragPipelineSettings && isLoading ? (
          <Row justify="center">
            <Col span={24}>
              <Spin className="d-block" size="large" spinning />
            </Col>
          </Row>
        ) : (
          <Row className="h-100" align="middle" justify="center">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('Please fill in the Provider Settings to access the NLP Configuration.')}
              style={{ maxWidth: 300 }}
            />
          </Row>
        )
      }
    </Fragment>
  );
});

HelviaRAGPipelineProviderConfigurationForm.propTypes = {
  ragPipelineSettings: PropTypes.object,
  ragPipelineSettingsForm: PropTypes.object,
  isLoading: PropTypes.bool,
  onSubmit: PropTypes.func,
  setHasPendingChanges: PropTypes.func,
  updateState: PropTypes.func
};

export { HelviaRAGPipelineProviderForm, HelviaRAGPipelineProviderConfigurationForm };
